import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Switch,
  Text,
  Textarea
} from '@chakra-ui/react'

import { FormField } from '../../../../../components/FormField'
import { MoneyInput } from '../../../../../components/MaskedInput'
import { SearchableSelect } from '../../../../../components/Select'
import { Tooltip } from '../../../../../components/Tooltip'
import { TicketType } from '../../../../../services/Event/types'
import { DoorSaleTicketFields } from '../../DoorSaleTicketFields'
import { EditionForm, SeriesForm, TemplateForm } from './eventSeriesFormShared'

interface IProps {
  form: SeriesForm
  patchSeries: (patch: Partial<SeriesForm>) => void
  fieldErrors: Record<string, string[]>
  saving: boolean
  addTemplate: (type: TicketType) => void
  updateTemplate: (index: number, patch: Partial<TemplateForm>) => void
  removeTemplate: (index: number) => void
  setCaucaoInfoOpen: (open: boolean) => void
  editionNumber: (edition: EditionForm) => number
  editionOptionLabel: (ed: EditionForm) => string
}

export function TicketsTab({
  form,
  patchSeries,
  fieldErrors,
  saving,
  addTemplate,
  updateTemplate,
  removeTemplate,
  setCaucaoInfoOpen,
  editionNumber,
  editionOptionLabel
}: IProps) {
  return (
    <>
      <Text variant="caption" mb={4}>
        Cada ingresso é criado de forma independente para cada edição selecionada (estoque separado por edição).
      </Text>

      {form.editions.length === 0 ? (
        <Text variant="caption">Adicione ao menos uma edição para criar ingressos.</Text>
      ) : (
        <Flex gap={3} mb={5}>
          <Button size="sm" colorScheme="blue" variant="outline" onClick={() => addTemplate('PAID')} disabled={saving}>
            + Ingresso Pago
          </Button>
          <Button size="sm" colorScheme="green" variant="outline" onClick={() => addTemplate('FREE')} disabled={saving}>
            + Ingresso Gratuito
          </Button>
        </Flex>
      )}

      <Flex direction="column" gap={4}>
        {form.ticket_templates.map((tpl, idx) => (
          <Box key={idx} border="1px solid" borderColor="gray.200" borderRadius="md" p={4}>
            <Flex justifyContent="space-between" alignItems="center" mb={3}>
              <Text fontWeight="bold" fontSize="sm">
                {tpl.type === 'FREE' ? '🟢 Ingresso Gratuito' : '🔵 Ingresso Pago'} #{idx + 1}
              </Text>
              <Flex gap={4} alignItems="center">
                {tpl.type === 'PAID' && (
                  <Flex alignItems="center" gap={2}>
                    <FormField label="Taxa isenta" mb={0}>
                      <Switch
                        isChecked={tpl.fee_exempt}
                        onChange={(e) => updateTemplate(idx, { fee_exempt: e.target.checked })}
                        isDisabled={saving}
                      />
                    </FormField>
                  </Flex>
                )}
                {tpl.type === 'FREE' && tpl.price > 0 && (
                  <Text fontSize="xs" color="orange.500" fontWeight="500">
                    Caução reembolsável
                  </Text>
                )}
                <Flex alignItems="center" gap={2}>
                  <FormField label="Ingresso Infantil" htmlFor={`template-${idx}-is_child_ticket`} mb={0}>
                    <Switch
                      id={`template-${idx}-is_child_ticket`}
                      isChecked={tpl.is_child_ticket}
                      onChange={(e) => updateTemplate(idx, { is_child_ticket: e.target.checked })}
                      colorScheme="purple"
                      size="sm"
                      isDisabled={saving}
                    />
                  </FormField>
                </Flex>
                <Flex alignItems="center" gap={2}>
                  <FormField label="Visível" mb={0}>
                    <Switch
                      isChecked={tpl.visible}
                      onChange={(e) => updateTemplate(idx, { visible: e.target.checked })}
                      isDisabled={saving}
                    />
                  </FormField>
                </Flex>
                <Button
                  size="xs"
                  colorScheme="red"
                  variant="ghost"
                  onClick={() => removeTemplate(idx)}
                  disabled={saving}
                >
                  Remover
                </Button>
              </Flex>
            </Flex>

            <Flex direction="column" gap={3}>
              {tpl.is_child_ticket && (
                <Box bg="purple.50" border="1px solid" borderColor="purple.200" borderRadius="md" p={3}>
                  <Text fontSize="xs" fontWeight="600" color="purple.700">
                    Ingresso Infantil
                  </Text>
                  <Text fontSize="xs" color="purple.700" mt={1}>
                    Não usa cartão NFC no check-in e exige validação da equipe na entrada. Vale para todas as edições
                    deste ingresso.
                  </Text>
                </Box>
              )}
              <Flex gap={3}>
                <FormField
                  label="Título do Ingresso"
                  error={fieldErrors[`ticket_templates.${idx}.title`]?.[0]}
                  isRequired
                  flex="2"
                >
                  <Input
                    size="sm"
                    value={tpl.title}
                    onChange={(e) => updateTemplate(idx, { title: e.target.value })}
                    disabled={saving}
                  />
                </FormField>
                <FormField
                  label="Quantidade"
                  error={fieldErrors[`ticket_templates.${idx}.quantity`]?.[0]}
                  isRequired
                  flex="1"
                >
                  <Input
                    size="sm"
                    type="number"
                    min={1}
                    value={tpl.quantity}
                    onChange={(e) =>
                      updateTemplate(idx, { quantity: e.target.value === '' ? '' : Number(e.target.value) })
                    }
                    disabled={saving}
                  />
                </FormField>
                <FormControl flex="1" isInvalid={!!fieldErrors[`ticket_templates.${idx}.price`]}>
                  {tpl.type === 'FREE' ? (
                    <Tooltip label="Preencha R$ 0,00 para ingresso totalmente gratuito. Para cobrar caução anti-desistência, preencha um valor ≥ R$ 5,00. O valor será reembolsado ao participante após check-in no evento.">
                      <FormLabel
                        fontSize="sm"
                        textDecoration="underline"
                        textDecorationStyle="dotted"
                        textDecorationColor="gray.400"
                        cursor="help"
                      >
                        Preço
                      </FormLabel>
                    </Tooltip>
                  ) : (
                    <FormLabel fontSize="sm">Preço</FormLabel>
                  )}
                  <MoneyInput
                    size="sm"
                    value={tpl.price}
                    onChange={(price) => updateTemplate(idx, { price: price ?? 0 })}
                    isDisabled={saving}
                  />
                  <FormErrorMessage>{fieldErrors[`ticket_templates.${idx}.price`]?.[0]}</FormErrorMessage>
                </FormControl>
              </Flex>

              <Flex gap={3}>
                <FormField
                  label="Início das Vendas"
                  error={fieldErrors[`ticket_templates.${idx}.sales_start`]?.[0]}
                  isRequired
                  flex="1"
                >
                  <Input
                    size="sm"
                    type="datetime-local"
                    value={tpl.sales_start}
                    onChange={(e) => updateTemplate(idx, { sales_start: e.target.value })}
                    disabled={saving}
                  />
                </FormField>
                <FormField
                  label="Término das Vendas"
                  error={fieldErrors[`ticket_templates.${idx}.sales_end`]?.[0]}
                  isRequired
                  flex="1"
                >
                  <Input
                    size="sm"
                    type="datetime-local"
                    value={tpl.sales_end}
                    min={tpl.sales_start}
                    onChange={(e) => updateTemplate(idx, { sales_end: e.target.value })}
                    disabled={saving}
                  />
                </FormField>
                <FormField
                  label="Máximo por compra"
                  error={fieldErrors[`ticket_templates.${idx}.max_per_purchase`]?.[0]}
                  isRequired
                  flex="1"
                >
                  <Input
                    size="sm"
                    type="number"
                    min={1}
                    value={tpl.max_per_purchase}
                    onChange={(e) =>
                      updateTemplate(idx, { max_per_purchase: e.target.value === '' ? '' : Number(e.target.value) })
                    }
                    disabled={saving}
                  />
                </FormField>
              </Flex>

              {tpl.type === 'PAID' && (
                <DoorSaleTicketFields
                  idPrefix={`template-${idx}`}
                  sellAtDoor={tpl.sell_at_door}
                  doorPrice={tpl.door_price}
                  error={fieldErrors[`ticket_templates.${idx}.door_price`]?.[0]}
                  saving={saving}
                  onChange={(patch) => updateTemplate(idx, patch)}
                />
              )}

              <Box>
                <FormField label="Descrição do Ingresso">
                  <Textarea
                    size="sm"
                    value={tpl.description}
                    placeholder="Este ingresso dá direito a..."
                    onChange={(e) => updateTemplate(idx, { description: e.target.value })}
                    disabled={saving}
                    rows={2}
                  />
                </FormField>
              </Box>

              <FormField
                label="Aplicar às edições"
                error={fieldErrors[`ticket_templates.${idx}.edition_indexes`]?.[0]}
                isRequired
              >
                <SearchableSelect<number>
                  isMulti
                  // Cards keep the array order (reordering them mid-typing would
                  // steal focus), so at least list the options in edition order.
                  options={form.editions
                    .map((ed, i) => ({ ed, value: i }))
                    .sort((a, b) => editionNumber(a.ed) - editionNumber(b.ed))
                    .map(({ ed, value }) => ({ value, label: editionOptionLabel(ed) }))}
                  value={tpl.edition_indexes.filter((i) => i < form.editions.length)}
                  onChange={(editionIndexes) => updateTemplate(idx, { edition_indexes: editionIndexes })}
                  placeholder="Selecione as edições"
                  entity="edição"
                  feminine
                  isDisabled={saving}
                />
              </FormField>

              {tpl.type === 'FREE' && tpl.price > 0 && (
                <Box bg="orange.50" border="1px solid" borderColor="orange.200" borderRadius="md" p={3}>
                  <Flex justifyContent="space-between" alignItems="flex-start" gap={3}>
                    <Box>
                      <Text fontSize="xs" fontWeight="600" color="orange.700">
                        Caução reembolsável — cobrança à vista
                      </Text>
                      <Text fontSize="xs" color="orange.700" mt={1}>
                        Cobrada no checkout (PIX ou cartão em 1x — o parcelamento é desabilitado) e reembolsada ao
                        participante após o check-in. Vale para todas as edições deste ingresso.
                      </Text>
                    </Box>
                    <Button
                      size="xs"
                      variant="outline"
                      colorScheme="orange"
                      flexShrink={0}
                      onClick={() => setCaucaoInfoOpen(true)}
                    >
                      ⓘ Entenda
                    </Button>
                  </Flex>
                </Box>
              )}
            </Flex>
          </Box>
        ))}
      </Flex>

      {form.ticket_templates.some((t) => t.type === 'PAID') && (
        <FormControl
          mt={5}
          isInvalid={
            !!fieldErrors['min_installment_value'] ||
            (form.min_installment_value !== null && form.min_installment_value < 5)
          }
        >
          <FormLabel htmlFor="min_installment_value">Valor mínimo de parcela</FormLabel>
          <MoneyInput
            id="min_installment_value"
            value={form.min_installment_value !== null ? Number(form.min_installment_value) : null}
            onChange={(min_installment_value) => patchSeries({ min_installment_value })}
            isDisabled={saving}
          />
          {form.min_installment_value !== null && form.min_installment_value < 5 ? (
            <FormErrorMessage>O valor mínimo de parcela deve ser pelo menos R$ 5,00.</FormErrorMessage>
          ) : (
            fieldErrors['min_installment_value']?.[0] && (
              <FormErrorMessage>{fieldErrors['min_installment_value'][0]}</FormErrorMessage>
            )
          )}
        </FormControl>
      )}
    </>
  )
}
